/**
 * <free-shipping-status> — D-17.3: the PDP buybox line showing how much is
 * missing to free shipping, cart-aware (threshold − cart.total_price).
 *
 * READ-ONLY BY CONSTRUCTION (R-01): the server renders the initial state from
 * cart.total_price; this element never fetches on load and never writes to the
 * cart. It re-renders only after a cart mutation EVENT (Dawn pubsub
 * `cart-update`, document `cart-quantity-updated` — all user-initiated):
 *   1. the event payload's total_price when present (cart.js change/update
 *      responses carry the full cart shape),
 *   2. otherwise one deduplicated read of the cart (CartItems.fetchCartData —
 *      /cart/add.js responses carry only the added line, not the aggregates),
 *   3. falling back to a plain /cart.js read when cart.js is not on the page.
 *
 * Strings arrive server-localized via data- attributes; the remaining-amount
 * template carries an [AMOUNT] placeholder (the cart-progress [PRICE]
 * convention). Money formatting reads the shop's money format from
 * <body data-money-format> — the same self-contained pattern as
 * promo-products.js (§5: no first-party import graph).
 */
if (!customElements.get('free-shipping-status')) {
  class FreeShippingStatus extends HTMLElement {
    connectedCallback() {
      this.textElement = this.querySelector('[data-free-shipping-text]');
      this.barElement = this.querySelector('[data-free-shipping-bar]');
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.moneyFormat = document.body.getAttribute('data-money-format') || '€{{ amount_with_comma_separator }}';

      this.pendingEvent = null;
      this.refreshDebounced =
        typeof debounce === 'function'
          ? debounce(() => this.refreshFromCart(), 150)
          : () => this.refreshFromCart();
      this.onCartEvent = (event) => {
        this.pendingEvent = event || null;
        this.refreshDebounced();
      };

      this.cartUpdateUnsubscriber =
        typeof subscribe === 'function'
          ? subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartEvent(event))
          : undefined;
      this.quantityListener = () => this.onCartEvent(null);
      document.addEventListener('cart-quantity-updated', this.quantityListener);
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      document.removeEventListener('cart-quantity-updated', this.quantityListener);
    }

    readTotal(payload) {
      return payload && typeof payload.total_price === 'number' ? payload.total_price : undefined;
    }

    refreshFromCart() {
      const event = this.pendingEvent;
      this.pendingEvent = null;
      const known = event ? this.readTotal(event.cartData) ?? this.readTotal(event.cart) : undefined;
      if (typeof known === 'number') {
        this.render(known);
        return;
      }
      const pending =
        typeof CartItems !== 'undefined' && CartItems.fetchCartData
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.js`)
              .then((response) => response.json())
              .catch(() => null);
      pending.then((cart) => {
        const total = this.readTotal(cart);
        if (typeof total === 'number') this.render(total);
      });
    }

    render(cartTotal) {
      if (!this.textElement) return;
      const unlocked = this.threshold > 0 && cartTotal >= this.threshold;
      this.classList.toggle('free-shipping-status--unlocked', unlocked);
      this.textElement.textContent = unlocked
        ? this.dataset.textUnlocked
        : (this.dataset.textRemaining || '').replace('[AMOUNT]', this.formatMoney(this.threshold - cartTotal));
      // D-21.10: the meter fill mirrors the text (cart-progress idiom)
      if (this.barElement) {
        const percent = unlocked ? 100 : this.threshold > 0 ? Math.min(100, (cartTotal / this.threshold) * 100) : 0;
        this.barElement.style.width = `${percent}%`;
      }
    }

    formatWithDelimiters(cents, precision, thousands, decimal) {
      const fixed = (cents / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    formatMoney(cents) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = this.moneyFormat.match(placeholderRegex);
      if (!match) return String(cents);
      let value = '';
      switch (match[1]) {
        case 'amount':
          value = this.formatWithDelimiters(cents, 2, ',', '.');
          break;
        case 'amount_no_decimals':
          value = this.formatWithDelimiters(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = this.formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = this.formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = this.formatWithDelimiters(cents, 0, ' ', '.');
          break;
        default:
          value = this.formatWithDelimiters(cents, 2, ',', '.');
      }
      return this.moneyFormat.replace(placeholderRegex, value);
    }
  }

  customElements.define('free-shipping-status', FreeShippingStatus);
}
